/** Hook that gives every color picker in the editor a shared,
 *  persistent view of the user's recent colors.
 *
 *  Why a module-level subscription:
 *    The Properties panel renders several `ColorSwatchPopover`
 *    instances at once (fill, stroke, brush, etc.). Persisting via
 *    `pushRecentColor` writes to localStorage, but each picker's
 *    own state would otherwise lag behind a push from a sibling.
 *    A trivial in-tab pub/sub keeps them in sync: pushing notifies
 *    every mounted subscriber, which re-reads from localStorage.
 *
 *  Cross-tab sync is intentionally NOT implemented — the editor
 *  is single-tab in practice, and a `storage`-event subscription
 *  would re-render every picker on every change in every other
 *  tab. Bump the storage key if a future feature actually needs
 *  multi-tab coordination. */

import { useCallback, useEffect, useState } from 'react';
import { loadRecentColors, pushRecentColor, clearRecentColors } from './recent-colors-prefs';

type Listener = (colors: string[]) => void;

const listeners = new Set<Listener>();

function notify(next: string[]): void {
  listeners.forEach((l) => l(next));
}

/** Subscribe + return current recents. The setter pushes to the
 *  persisted list and notifies every other mounted subscriber. */
export function useRecentColors(): {
  recentColors: string[];
  pushRecent: (color: string) => void;
  clearRecents: () => void;
} {
  const [recents, setRecents] = useState<string[]>(() => loadRecentColors());

  useEffect(() => {
    listeners.add(setRecents);
    return () => {
      listeners.delete(setRecents);
    };
  }, []);

  const pushRecent = useCallback((color: string) => {
    const next = pushRecentColor(color);
    notify(next);
  }, []);

  const clearRecents = useCallback(() => {
    clearRecentColors();
    notify([]);
  }, []);

  return { recentColors: recents, pushRecent, clearRecents };
}

/** Reset the in-module listener set + storage. For tests only. */
export function __resetRecentColorsForTests(): void {
  listeners.clear();
  clearRecentColors();
}
